import { z } from "zod";
import { useLocation } from "wouter";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/form/text-input";
import { Form } from "@/components/ui/form/form";
import { useModalError } from "@/hooks/use-modal-error";
import { useSegment } from "@/hooks/use-segment";
import { useCreateContextPack } from "@/api/studio/contexts/queries";
import { createContextPackSchema } from "@/api/studio/contexts/types/context.type";
type Form = z.infer<typeof createContextPackSchema>;

/* Prop Types */
interface CreateContextStudyPackModalProps {
  projectId: string;
  studyId: string;
  useCaseId?: string;
  useCaseName?: string;
}

/*
 * CreateContextStudyPackModal
 * Modal to create a new context pack inside the study flow
 */
export const CreateContextStudyPackModal = ({ projectId, studyId, useCaseId, useCaseName }: CreateContextStudyPackModalProps) => {
  /* Stores */
  const [, navigate] = useLocation();

  /* Queries */
  const { mutateAsync: createContextPack, isPending: isCreateContextPackPending, isError: isCreateContextPackError } = useCreateContextPack();

  /* Hooks */
  const { close } = useModalError({ error: isCreateContextPackError });
  const { segmentTrackEvent } = useSegment();

  /* Handlers */
  const handleCreate = async ({ name, useCaseId, useCaseName }: Form) => {
    const res = await createContextPack({ name, useCaseId, useCaseName });

    // Segment Tracking
    segmentTrackEvent("Context Pack Created", {
      name: name,
      useCaseId: useCaseId,
      useCaseName: useCaseName,
      origin: "Study",
    });

    // Navigate to the next view
    navigate(`/projects/${projectId}/studies/${studyId}/asset/study-context/${res.pack.id}/select`);
    close();
  };

  /* Render */
  return (
    <Form schema={createContextPackSchema} onSubmit={handleCreate}>
      {({ register, formState: { errors } }) => (
        <>
          <div className="flex items-center justify-between pb-6">
            <h2>New context title</h2>
            <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
          </div>

          <p className="opacity-70">
            Tip: A great context title might include your market, or marketing channels this context relates to. For example, “Top 10 Retailers in US 2025.”
          </p>

          {/* Context pack title */}
          <TextInput label="Title" registration={register("name")} error={errors.name?.message} className="w-full" placeholder="Context name" />

          {/* Hidden inputs to save useCases from the previous step */}
          <TextInput type="hidden" registration={register("useCaseId")} error={errors.useCaseId?.message} value={useCaseId} />
          <TextInput type="hidden" registration={register("useCaseName")} error={errors.useCaseName?.message} value={useCaseName} />

          {/* Nav buttons */}
          <div className="sticky bottom-0 flex justify-end bg-base-100 py-4 translate-y-6">
            <Button type="button" className="mr-2" onClick={close}>
              Back
            </Button>
            <Button type="submit" variant="primary" disabled={isCreateContextPackPending}>
              {isCreateContextPackPending ? "Creating..." : "Continue"}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};
